import React, { useState, useEffect } from 'react';
import { WeatherData } from '../types';
import { shouldTriggerValidation, submitWeatherValidation } from '../services/weatherValidationService';

interface WeatherValidationProps {
  weather: WeatherData | null;
  location: string;
  isUserLocation: boolean; // Only show if this is the user's real location
}

const WeatherValidation: React.FC<WeatherValidationProps> = ({ weather, location, isUserLocation }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (isUserLocation && weather && !isSubmitted) {
      const shouldShow = shouldTriggerValidation(weather, location);
      // Add a small delay so it doesn't pop up instantly on load
      if (shouldShow) {
        const timer = setTimeout(() => setIsVisible(true), 3000);
        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
  }, [weather, location, isUserLocation, isSubmitted]);

  const handleResponse = async (isRaining: boolean) => {
    if (!weather) return;

    setIsVisible(false);
    
    // Optimistic UI update - show thanks toast or similar if needed
    await submitWeatherValidation(location, weather, isRaining);
    
    setIsSubmitted(true);
    
    // Reset submitted state after a long time? No, rely on service cooldown.
  };

  if (!isVisible || !weather) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-24 z-50 animate-in slide-in-from-bottom-4 duration-500">
      <div className="bg-white/95 backdrop-blur-md border border-blue-100 shadow-xl rounded-2xl p-4 max-w-sm ml-auto relative overflow-hidden">
        {/* Decorative background element */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-400 to-cyan-300 opacity-10 rounded-bl-full -mr-4 -mt-4"></div>
        
        <div className="relative z-10">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h4 className="font-bold text-gray-800 text-sm">Validasi Cuaca</h4>
              <p className="text-[10px] text-gray-500">Bantu kami meningkatkan akurasi.</p>
            </div>
            <button 
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              ✕
            </button>
          </div>
          
          <p className="text-xs text-gray-700 font-medium mb-3">
            Apakah saat ini di <span className="text-blue-600 font-bold">{location}</span> sedang hujan?
          </p>

          <div className="flex gap-2">
            <button
              onClick={() => handleResponse(true)}
              className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 py-2 rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-1.5"
            >
              <span>🌧️</span> Ya, Hujan
            </button>
            <button
              onClick={() => handleResponse(false)}
              className="flex-1 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 py-2 rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-1.5"
            >
              <span>☁️</span> Tidak
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherValidation;
